import { type ApiValidationFieldError } from "@/lib/api-validation-errors";

import { type ServerFieldErrors } from "../store/payment-method-form-slice";

import type { ExtendedForm } from "../hooks/use-available-payment-methods";

export interface ResolvedServerFieldErrors {
  byForm: ServerFieldErrors;
  unresolved: ApiValidationFieldError[];
}

const collectFormFields = (form: ExtendedForm) => {
  const fieldsByForm = new Map<string, Set<string>>();

  let current: ExtendedForm | undefined = form;
  while (current) {
    fieldsByForm.set(
      current.name,
      new Set(current.fields.map((field) => field.property_name))
    );
    current = current.next;
  }

  return fieldsByForm;
};

const locate = (
  path: string[],
  rootFormName: string,
  fieldsByForm: Map<string, Set<string>>
) => {
  const formName = path.length === 1 ? rootFormName : path[path.length - 2];
  const fieldName = path[path.length - 1];

  if (!fieldsByForm.get(formName)?.has(fieldName)) {
    return null;
  }

  return { formName, fieldName };
};

export const resolveServerFieldErrors = (
  form: ExtendedForm,
  fieldErrors: ApiValidationFieldError[]
): ResolvedServerFieldErrors => {
  const fieldsByForm = collectFormFields(form);
  const byForm: ServerFieldErrors = {};
  const unresolved: ApiValidationFieldError[] = [];

  for (const fieldError of fieldErrors) {
    const location =
      fieldError.path.length > 0
        ? locate(fieldError.path, form.name, fieldsByForm)
        : null;

    if (!location) {
      unresolved.push(fieldError);
      continue;
    }

    byForm[location.formName] = {
      ...byForm[location.formName],
      [location.fieldName]: fieldError.message,
    };
  }

  return { byForm, unresolved };
};

export const findFirstFormWithErrors = (
  form: ExtendedForm,
  byForm: ServerFieldErrors
): string | undefined => {
  let current: ExtendedForm | undefined = form;
  while (current) {
    if (byForm[current.name]) {
      return current.name;
    }
    current = current.next;
  }

  return undefined;
};
